import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Users, UserCheck, MapPin, Mail, Send } from "lucide-react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import FormControl from "../../common/FormControl";
import Toast, { ToastMessage } from "../../common/Toast";
import NoDataFound from "../../common/NoDataFound";
import {
    governanceStakeholdersService,
    type DerivedStakeholdersResponse,
    type DerivedStakeholderAuthority,
    type StepType,
} from "../../../services/governanceService";
import { extractApiError } from "../../../utils/apiError";

interface Props {
    processId: number;
    /** Stakeholders is ALWAYS read-only — kept for prop-shape symmetry only. */
    readOnly?: boolean;
}

const STEP_TYPE_COLOR: Record<string, string> = {
    PERFORM: "bg-emerald-50 text-emerald-700 border-emerald-200",
    APPROVE: "bg-purple-50 text-purple-700 border-purple-200",
    REVIEW:  "bg-amber-50 text-amber-700 border-amber-200",
    NOTIFY:  "bg-blue-50 text-blue-700 border-blue-200",
    OTHER:   "bg-gray-100 text-gray-700 border-gray-300",
};

// Derived read-only stakeholder roll-up for an SOP.
// Layout (top → bottom):
//   1. Three metric tiles: authorities, people, postings.
//   2. Single combined table: one row per posting, with authority + engagement
//      cells spanning continuations. Click authority → modal showing the
//      steps that authority covers in this SOP.
//   3. Footer note explaining that the view is derived (single source of
//      truth = step authorities).
// Step lists deliberately pushed into a per-authority modal — surfacing them
// at top level was overwhelming.
const SopStakeholdersPanel: React.FC<Props> = ({ processId }) => {
    const [data, setData] = useState<DerivedStakeholdersResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stepsForAuthority, setStepsForAuthority] = useState<DerivedStakeholderAuthority | null>(null);
    // Mail composer state — UI-only placeholder. Mail integration is not
    // wired; the Send button just shows a toast so we can validate the flow.
    const [composeOpen, setComposeOpen] = useState(false);
    const [mailSubject, setMailSubject] = useState("");
    const [mailBody, setMailBody] = useState("");
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await governanceStakeholdersService.getDerived(processId);
            setData(resp);
        } catch (e: any) {
            setError(extractApiError(e, "Failed to load stakeholders."));
        } finally {
            setLoading(false);
        }
    }, [processId]);

    useEffect(() => { load(); }, [load]);

    // Metric counts: authorities / people (distinct users) / postings (rows
    // that have an assigned user).
    const metrics = useMemo(() => {
        if (!data) return { authorities: 0, people: 0, postings: 0 };
        const userIds = new Set<number>();
        let postings = 0;
        for (const a of data.authorities) {
            for (const p of a.postings) {
                if (p.user_id != null) {
                    userIds.add(p.user_id);
                    postings += 1;
                }
            }
        }
        return {
            authorities: data.authorities.length,
            people: userIds.size,
            postings,
        };
    }, [data]);

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading stakeholders…
            </div>
        );
    }
    if (error) return <div className="p-3 text-sm text-red-600">{error}</div>;
    if (!data || data.authorities.length === 0) {
        return (
            <NoDataFound
                title="No Authorities Engaged"
                message="No authorities are engaged on this SOP yet. Add a step with an authority to populate this view."
            />
        );
    }

    return (
        <div className="flex flex-col gap-y-5">
            {/* Header row: metric tiles + a mail-everyone nudge. The nudge
                opens a compose modal seeded with the recipient list; actual
                send is mocked because mail integration isn't wired yet. */}
            <div className="flex items-stretch gap-3">
                <div className="grid grid-cols-3 gap-3 flex-1">
                    <MetricTile icon={<Users size={16} className="text-emerald-600" />}    label="Authorities" value={metrics.authorities} />
                    <MetricTile icon={<UserCheck size={16} className="text-blue-600" />}    label="People"      value={metrics.people} />
                    <MetricTile icon={<MapPin size={16} className="text-amber-600" />}      label="Postings"    value={metrics.postings} />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setMailSubject(`Update on SOP: ${data.process_name}`);
                        setMailBody("");
                        setComposeOpen(true);
                    }}
                    disabled={metrics.people === 0}
                    data-testid="BTN_sop_stakeholders_notify_open"
                    title={metrics.people === 0
                        ? "No reachable recipients yet — assign users to the postings under these authorities."
                        : `Notify ${metrics.people} ${metrics.people === 1 ? "person" : "people"}`}
                    className={`flex flex-col items-center justify-center gap-1 px-4 rounded-lg border ${
                        metrics.people === 0
                            ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                    style={{ minWidth: 120 }}
                >
                    <Mail size={20} />
                    <span className="text-[11px] uppercase tracking-wider font-semibold">Notify</span>
                    <span className="text-[10px] text-gray-500">{metrics.people} {metrics.people === 1 ? "person" : "people"}</span>
                </button>
            </div>

            {/* Combined table — one row per posting */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Authority</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Engagement</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Location</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Person</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Email</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.authorities.flatMap(a => {
                            // If an authority has no postings we still show a single row
                            // with the authority name + a "no positions" placeholder.
                            const rowsForAuth = a.postings.length === 0 ? [null] : a.postings;
                            return rowsForAuth.map((p, i) => (
                                <tr
                                    key={`a-${a.authority_id}-r-${i}`}
                                    className="border-b border-gray-100 last:border-b-0"
                                >
                                    {/* Authority cell (clickable) — blank on continuation rows */}
                                    <td className="px-3 py-2 align-top">
                                        {i === 0 ? (
                                            <button
                                                type="button"
                                                className="text-left font-medium text-gray-800 hover:text-emerald-700 underline-offset-2 hover:underline"
                                                onClick={() => setStepsForAuthority(a)}
                                                title="See which steps engage this authority"
                                                data-testid={`BTN_sop_stakeholders_authority_${a.authority_id}_open`}
                                            >
                                                {a.authority_name}
                                            </button>
                                        ) : (
                                            <span aria-hidden> </span>
                                        )}
                                    </td>
                                    {/* Engagement chips — only on the first row per authority */}
                                    <td className="px-3 py-2 align-top">
                                        {i === 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {a.engagements.map(e => (
                                                    <span
                                                        key={e}
                                                        className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${STEP_TYPE_COLOR[e as StepType] || STEP_TYPE_COLOR.OTHER}`}
                                                    >
                                                        {e}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    {/* Location | Person | Email — per posting, or a "no positions" cell if none */}
                                    {p === null ? (
                                        <td className="px-3 py-2 text-gray-400 italic text-xs" colSpan={3}>
                                            No positions defined for this authority.
                                        </td>
                                    ) : (
                                        <>
                                            <td className="px-3 py-2 text-gray-700">{p.location_name}</td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {p.user_name || <span className="text-gray-400 italic text-xs">Vacant</span>}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600 text-xs">
                                                {p.email || <span className="text-gray-400 italic">—</span>}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ));
                        })}
                    </tbody>
                </table>
            </div>

            <div className="text-[11px] text-gray-500 italic">
                Read-only — derived from SOP step authorities. Click an authority to see which steps engage it.
            </div>

            {/* Compose-and-notify modal. Mail integration is not implemented:
                Send just toasts a confirmation so the UX flow can be reviewed.
                Recipient list shown as a read-only summary of who would
                receive the mail. */}
            <Modal isOpen={composeOpen} onClose={() => setComposeOpen(false)} size="lg" closeOnOverlayClick={false}>
                <Modal.Header>
                    Notify stakeholders — "<span className="item-highlight">{data.process_name}</span>"
                </Modal.Header>
                <Modal.Body>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                            Mail integration is not wired yet. This is a UI preview — Send will
                            confirm the action but no email is actually dispatched.
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                                Recipients ({data.recipients.length})
                            </div>
                            {data.recipients.length === 0 ? (
                                <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded p-2 bg-gray-50">
                                    No reachable recipients — assign users to the postings under the involved authorities first.
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto p-2 border border-gray-200 rounded bg-gray-50">
                                    {data.recipients.map(r => (
                                        <span key={r.user_id} className="inline-flex items-center text-[11px] text-gray-700 bg-white border border-gray-200 rounded px-2 py-0.5">
                                            {r.user_name} <span className="text-gray-400 ml-1">&lt;{r.email}&gt;</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <FormControl.Input
                            label="Subject"
                            value={mailSubject}
                            onChange={(e) => setMailSubject(e.target.value)}
                            placeholder="What is this notification about?"
                            data-testid="INP_sop_stakeholders_notify_subject"
                        />
                        <FormControl.Textarea
                            label="Message"
                            value={mailBody}
                            onChange={(e) => setMailBody(e.target.value)}
                            rows={8}
                            placeholder="Write the change summary or any context the recipients need."
                            data-testid="INP_sop_stakeholders_notify_body"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="fill"
                        icon={Send}
                        onClick={() => {
                            setToast({
                                type: "success",
                                message: `Would have notified ${data.recipients.length} ${data.recipients.length === 1 ? "person" : "people"} (mail integration pending).`,
                            });
                            setComposeOpen(false);
                        }}
                        disabled={!mailSubject.trim() || data.recipients.length === 0}
                        data-testid="BTN_sop_stakeholders_notify_send"
                    >
                        Send
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />

            {/* Per-authority step list — modal kept lightweight on purpose. */}
            <Modal
                isOpen={!!stepsForAuthority}
                onClose={() => setStepsForAuthority(null)}
                size="md"
            >
                <Modal.Header>
                    {stepsForAuthority ? (
                        <>Steps engaging "<span className="item-highlight">{stepsForAuthority.authority_name}</span>"</>
                    ) : "Steps"}
                </Modal.Header>
                <Modal.Body>
                    {stepsForAuthority && (
                        <div className="border border-gray-200 rounded overflow-hidden bg-white">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Step</th>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 w-28">Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stepsForAuthority.steps.map(s => (
                                        <tr key={s.step_id} className="border-b border-gray-100 last:border-b-0">
                                            <td className="px-3 py-2 text-gray-700">{s.step_number}</td>
                                            <td className="px-3 py-2 text-gray-800">{s.step_name}</td>
                                            <td className="px-3 py-2">
                                                <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${STEP_TYPE_COLOR[s.step_type as StepType] || STEP_TYPE_COLOR.OTHER}`}>
                                                    {s.step_type}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

const MetricTile: React.FC<{ icon: React.ReactNode; label: string; value: number }> = ({ icon, label, value }) => (
    <div className="border border-gray-200 rounded-lg bg-white p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center">{icon}</div>
        <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
            <span className="text-xl font-semibold text-gray-800">{value}</span>
        </div>
    </div>
);

export default SopStakeholdersPanel;
